import { sendHighlightEvent } from '@hotjar/webapp/domains/highlights/api';
import type { UIPosition } from '@hotjar/webapp/domains/highlights/api';
import { HighlightsEventType } from '@hotjar/webapp/domains/highlights/dto';
import { useShortUrl } from '@hotjar/webapp/features/ShortLink/use-short-url';
import type { RequestState } from '@hotjar/webapp/features/ShortLink/use-short-url';
import { useCurrentSiteId } from '@hotjar/site';
import { selectText } from '@hotjar/webapp/utils/select-text';
import { useCallback, useMemo, useRef } from 'react';
import type { MutableRefObject } from 'react';

interface UseGetHighlightShortlink {
  selectAndCopy: () => void;
  outputRef: MutableRefObject<HTMLOutputElement | null>;
  shortUrl: RequestState;
}

export function useGetShortlink(
  highlightId?: string | null,
  uiPosition?: UIPosition,
): UseGetHighlightShortlink {
  const currentSiteId = useCurrentSiteId();
  const url = useMemo(() => {
    const parsedUrl = new URL(window.location.href);
    // NOTE: "heatmap URL" should contain the highlight param already at the
    // moment of rendering this modal; but sometimes it doesn't for some reason
    parsedUrl.searchParams.set('highlight', highlightId ?? '');
    return parsedUrl.toString();
  }, [highlightId]);
  const shortUrl = useShortUrl(currentSiteId ?? '', url);
  const outputRef = useRef<HTMLOutputElement | null>(null);
  const selectAndCopy = useCallback(() => {
    if (outputRef.current !== null) {
      selectText(outputRef.current);
    }

    if (shortUrl.status !== 'success') {
      return;
    }

    if (currentSiteId && highlightId) {
      sendHighlightEvent(
        currentSiteId,
        { highlightId, eventType: HighlightsEventType.HIGHLIGHT_SHARED },
        { uiPosition },
      );
    }
  }, [shortUrl, currentSiteId, highlightId, uiPosition]);

  return {
    selectAndCopy,
    outputRef,
    shortUrl,
  };
}
